import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBackgroundMusic } from '@/hooks/useBackgroundMusic';
import './LoadingScreen.css';

const MIN_DURATION = 2000; // Minimum 2s for branding

/** Preload critical images */
function preloadImages(): Promise<void> {
  const srcs = [
    '/Logo/logo-sem-fundo.png',
    '/splash/loadingscreen.jpg',
    '/splash/loadingscreen-h.jpg',
  ];
  return Promise.all(
    srcs.map(src => new Promise<void>(resolve => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    }))
  ).then(() => {});
}

/** Minimum timer promise */
function minTimer(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

interface LoadingScreenProps {
  onComplete: () => void;
}

export function LoadingScreen({ onComplete }: LoadingScreenProps) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);

  // Start background music (will persist to login page)
  useBackgroundMusic('/songs/login_madboat_music.mp3', {
    volume: 0.4,
    loop: true,
    autoPlay: true,
  });

  const stableOnComplete = useCallback(onComplete, []);

  useEffect(() => {
    let cancelled = false;

    // Track 3 milestones with weights
    const milestones = [
      { weight: 40, promise: document.fonts.ready },
      { weight: 30, promise: preloadImages() },
      { weight: 30, promise: minTimer(MIN_DURATION) },
    ];

    let completed = 0;

    milestones.forEach(({ weight, promise }) => {
      promise.then(() => {
        if (cancelled) return;
        completed += weight;
        setProgress(Math.min(completed, 100));
      });
    });

    Promise.all(milestones.map(m => m.promise)).then(() => {
      if (cancelled) return;
      setProgress(100);
      setFadeOut(true);
      setTimeout(stableOnComplete, 500);
    });

    return () => { cancelled = true; };
  }, [stableOnComplete]);

  return (
    <div className={`loading-screen ${fadeOut ? 'fade-out' : ''}`}>
      <div className="loading-background">
        <picture>
          <source
            srcSet="/splash/loadingscreen-h.jpg"
            media="(min-width: 768px)"
          />
          <img
            src="/splash/loadingscreen.jpg"
            alt="MadBoat Loading"
            className="loading-image"
          />
        </picture>
      </div>

      <div className="loading-bar-container">
        <span className="loading-percentage">
          {t('loading.loadingResources', { progress: Math.round(progress) })}
        </span>
        <div className="loading-bar-track">
          <div
            className="loading-bar-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}
